/**
 * AdminUserListPage
 * [UC-005] Admin View Users Use
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';
import { Select } from '../components/ui/Select';

interface User {
  userId: number;
  name: string;
  surname: string;
  email: string;
  role: string;
  status: string;
  creditBalance: number;
  registrationDate: string;
}

export function AdminUserListPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const navigate = useNavigate();
  const { t } = useTranslation();

  // Filter options
  const roleOptions = [
    { value: '', label: t('adminUserList.allRoles', 'All Roles') },
    { value: 'Internal System - Admin', label: t('adminUserList.admin', 'Admin') },
    { value: 'Internal System - User', label: t('adminUserList.user', 'User') }
  ];

  const statusOptions = [
    { value: '', label: t('adminUserList.allStatus', 'All Status') },
    { value: 'Active', label: t('adminUserList.active', 'Active') },
    { value: 'Deactivated', label: t('adminUserList.deactivated', 'Deactivated') }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchQuery, selectedRole, selectedStatus]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Default page size is 20; bump to the backend max (100) so the full
      // admin user table renders rather than silently capping at 20.
      const response = await api.get('/users', { params: { limit: 100 } });
      const userItems = (response as any)?.items || [];
      setUsers(userItems);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(t('adminUserList.loadError', 'Failed to load users'));
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = users;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(query) ||
        user.surname.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    }

    // Apply role filter
    if (selectedRole) {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(user => user.status === selectedStatus);
    }

    setFilteredUsers(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedRole('');
    setSelectedStatus('');
  };

  const handleUserClick = (userId: number) => {
    navigate(`/admin/users/${userId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--text-muted)]">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadUsers}
            className="px-4 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors"
          >
            {t('common.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">{t('adminUserList.title', 'User List')}</h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">{t('adminUserList.subtitle', 'Manage all users in the system')}</p>
      </div>

      {/* Filter Section */}
      <div className="mb-5 sm:mb-6 p-4 sm:p-4 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] overflow-visible">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
          {/* Search Input */}
          <div className="relative">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('adminUserList.searchUsers', 'Search Users')}</label>
            <div className="relative">
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                type="text"
                className="w-full px-4 py-3 sm:py-2.5 pl-10 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-base sm:text-sm"
                placeholder={t('adminUserList.searchPlaceholder', 'Search by name, email...')}
              />
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Role Filter */}
          <div>
            <Select<string>
              label={t('adminUserList.role', 'Role')}
              value={selectedRole}
              onChange={setSelectedRole}
              options={roleOptions}
            />
          </div>

          {/* Status Filter */}
          <div>
            <Select<string>
              label={t('adminUserList.status', 'Status')}
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={statusOptions}
            />
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && !searchQuery && !selectedRole && !selectedStatus ? (
        <div className="text-center py-12">
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-8">
            <svg className="mx-auto h-12 w-12 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">{t('adminUserList.noUsers', 'No users found')}</h3>
            <p className="mt-2 text-base sm:text-sm text-[var(--text-secondary)]">{t('adminUserList.noUsersDesc', 'Get started by creating your first user.')}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/admin/user-create')}
                className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors"
              >
                {t('adminUserList.createUser', 'Create User')}
              </button>
            </div>
          </div>
        </div>
      ) : filteredUsers.length === 0 ? (
        /* No Results State */
        <div className="text-center py-12">
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-8">
            <svg className="mx-auto h-12 w-12 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">{t('adminUserList.noMatchingUsers', 'No users match your filters')}</h3>
            <p className="mt-2 text-base sm:text-sm text-[var(--text-secondary)]">{t('adminUserList.adjustFilters', 'Try adjusting your search criteria or clear the filters.')}</p>
            <div className="mt-6">
              <button
                onClick={clearFilters}
                className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
              >
                {t('adminUserList.clearFilters', 'Clear Filters')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Users Table */
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-[var(--border-subtle)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)]">{t('adminUserList.users', 'Users')}</h2>
              <p className="text-sm text-[var(--text-secondary)]">
                {filteredUsers.length} {filteredUsers.length !== 1 ? t('adminUserList.usersFound', 'users found') : t('adminUserList.userFound', 'user found')}
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/user-create')}
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminUserList.createUser', 'Create User')}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-[var(--surface-raised)]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminUserList.colName', 'Name')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminUserList.colEmail', 'Email')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminUserList.colRole', 'Role')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminUserList.colStatus', 'Status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminUserList.colActions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {filteredUsers.map((user) => (
                  <tr
                    key={user.userId}
                    onClick={() => handleUserClick(user.userId)}
                    className="hover:bg-[var(--surface-raised)] cursor-pointer active:bg-[var(--surface-raised)] transition-colors"
                  >
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-primary)] whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-[var(--accent-primary)]/15 flex items-center justify-center">
                          <span className="text-sm font-medium text-[var(--accent-primary)]">
                            {user.name.charAt(0)}{user.surname.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium">{user.name} {user.surname}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-secondary)] whitespace-nowrap">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-secondary)] whitespace-nowrap">
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[var(--surface-overlay)] text-[var(--text-secondary)] border border-[var(--border-subtle)]">
                        {user.role === 'Internal System - Admin' ? t('adminUserList.admin', 'Admin') : t('adminUserList.user', 'User')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm whitespace-nowrap">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${
                        user.status === 'Active'
                          ? 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border-[var(--accent-primary)]/25'
                          : 'bg-red-600/15 text-red-700 dark:text-red-500 border-red-600/25'
                      }`}>
                        {user.status === 'Active' ? t('adminUserList.active', 'Active') : t('adminUserList.deactivated', 'Deactivated')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUserClick(user.userId);
                        }}
                        className="text-[var(--accent-primary)] hover:text-[var(--accent-secondary)] font-medium"
                      >
                        {t('adminUserList.viewDetails', 'View Details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-5 sm:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
        >
          {t('adminUserList.backToDashboard', 'Back to Dashboard')}
        </button>
      </div>
    </div>
  );
}

export default AdminUserListPage;
